import { useEffect, useState } from 'react';

interface HeaderProps {
  darkMode: boolean;
  onToggleDark: () => void;
}

export function Header({ darkMode, onToggleDark }: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-purple-900/40 bg-gray-950/90 backdrop-blur-sm">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
        <div className="flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 32 32"
            className="h-8 w-8 shrink-0"
            aria-hidden="true"
          >
            <rect width="32" height="32" rx="6" fill="#7c3aed" />
            <rect x="5" y="5" width="22" height="22" rx="3" fill="none" stroke="#c4b5fd" strokeWidth="2" />
            <rect x="10" y="10" width="12" height="12" rx="2" fill="none" stroke="#a78bfa" strokeWidth="1.5" />
            <rect x="14" y="14" width="4" height="4" rx="1" fill="#e9d5ff" />
          </svg>
          <span className="text-lg font-bold tracking-tight text-white">Nested Gameception</span>
        </div>

        <nav
          aria-label="Main navigation"
          className="hidden items-center gap-6 text-sm font-medium sm:flex"
        >
          <a href="#how-it-works" className="text-gray-300 hover:text-purple-400 transition-colors">
            How It Works
          </a>
          <a href="#layers" className="text-gray-300 hover:text-purple-400 transition-colors">
            Layers
          </a>
          <a
            href="#play"
            className="rounded-lg bg-purple-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-purple-700 transition-colors"
          >
            Play Now
          </a>
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            className="sm:hidden rounded-lg border border-gray-700 p-2 hover:bg-gray-800 transition-colors"
          >
            {menuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            )}
          </button>

          <button
            onClick={onToggleDark}
            aria-label="Toggle dark mode"
            className="rounded-lg border border-gray-700 p-2 text-sm hover:bg-gray-800 transition-colors"
          >
            {darkMode ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav
          id="mobile-nav"
          aria-label="Mobile navigation"
          className="sm:hidden border-t border-gray-800 px-4 py-3 flex flex-col gap-3 text-sm font-medium"
        >
          <a
            href="#how-it-works"
            onClick={() => setMenuOpen(false)}
            className="text-gray-300 hover:text-purple-400 transition-colors"
          >
            How It Works
          </a>
          <a
            href="#layers"
            onClick={() => setMenuOpen(false)}
            className="text-gray-300 hover:text-purple-400 transition-colors"
          >
            Layers
          </a>
          <a
            href="#play"
            onClick={() => setMenuOpen(false)}
            className="text-purple-400 hover:text-purple-300 transition-colors"
          >
            Play Now
          </a>
        </nav>
      )}
    </header>
  );
}
